import { forwardRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';

// Building blocks for the sign-in screens, matching the homeowner app
// (asg-homeowner-mobile-app src/components/ui.tsx and src/theme.ts).

// Outline icon paths (Heroicons, MIT).
const ICON_PATHS = {
  mail: 'M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75',
  inbox: 'M21.75 9v.906a2.25 2.25 0 01-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 001.183 1.981l6.478 3.488m8.839 2.51l-4.66-2.51m0 0l-1.023-.55a2.25 2.25 0 00-2.134 0l-1.022.55m0 0l-4.661 2.51m16.5 1.615a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V8.844a2.25 2.25 0 011.183-1.98l7.5-4.04a2.25 2.25 0 012.134 0l7.5 4.04a2.25 2.25 0 011.183 1.98V19.5z',
  done: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  clock: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  people: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  lock: 'M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z',
  fingerprint: 'M7.864 4.243A7.5 7.5 0 0119.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 004.5 10.5a7.464 7.464 0 01-1.15 3.993m1.989 3.559A11.209 11.209 0 008.25 10.5a3.75 3.75 0 117.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 01-3.6 9.75m6.633-4.596a18.666 18.666 0 01-2.485 5.33',
  info: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
  alert: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z',
  plus: 'M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z',
  eye: 'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
  eyeOff: 'M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88',
} as const;

export type IconName = keyof typeof ICON_PATHS;

export function Icon({ name, className = 'h-5 w-5' }: { name: IconName; className?: string }) {
  return <svg aria-hidden="true" className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d={ICON_PATHS[name]} />
  </svg>;
}

const BUTTON_VARIANTS = {
  primary: 'min-h-[54px] bg-asg-forest text-white hover:bg-asg-forest-deep',
  secondary: 'min-h-[54px] bg-asg-lime-soft text-asg-forest hover:bg-[#E3ECB0]',
  quiet: 'min-h-[44px] bg-transparent text-asg-forest hover:underline',
  danger: 'min-h-[54px] bg-asg-danger-soft text-asg-danger hover:bg-[#FAD9D5]',
} as const;

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: keyof typeof BUTTON_VARIANTS;
  icon?: IconName;
  loading?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', icon, loading = false, disabled, className = '', children, type = 'button', ...props }, ref,
) {
  return <button ref={ref} type={type} disabled={disabled || loading} aria-busy={loading || undefined}
    className={`flex w-full items-center justify-center gap-2 rounded-[14px] px-6 text-[17px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-asg-forest focus-visible:ring-offset-2 focus-visible:ring-offset-asg-paper disabled:cursor-not-allowed disabled:opacity-45 ${BUTTON_VARIANTS[variant]} ${className}`}
    {...props}>
    {loading
      ? <><span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" /><span className="sr-only">{children}</span></>
      : <>{icon && <Icon name={icon} />}{children}</>}
  </button>;
});

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  id: string;
  label: string;
  hint?: ReactNode;
  invalid?: boolean;
  /** Extra ids to read out with the field, e.g. an error notice. */
  describedBy?: string;
  /** A password field with a show/hide button. */
  secure?: boolean;
};

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { id, label, hint, invalid, describedBy, secure = false, className = '', type, ...props }, ref,
) {
  const [revealed, setRevealed] = useState(false);
  const hintId = hint ? `${id}-hint` : undefined;
  const described = [hintId, describedBy].filter(Boolean).join(' ') || undefined;
  return <div className="space-y-1.5 text-left">
    <label htmlFor={id} className="block text-[15px] font-semibold text-asg-ink">{label}</label>
    <div className="relative">
      <input ref={ref} id={id} type={secure ? (revealed ? 'text' : 'password') : type}
        aria-invalid={invalid || undefined} aria-describedby={described}
        className={`asg-input ${secure ? 'asg-input-secure' : ''} ${className}`} {...props} />
      {secure && <button type="button" onClick={() => setRevealed(value => !value)}
        aria-label={revealed ? 'Hide password' : 'Show password'} aria-pressed={revealed}
        className="absolute inset-y-0 right-0 flex w-14 items-center justify-center rounded-r-[14px] text-asg-slate hover:text-asg-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-asg-forest">
        <Icon name={revealed ? 'eyeOff' : 'eye'} className="h-6 w-6" />
      </button>}
    </div>
    {hint && <p id={hintId} className="text-[13px] leading-[18px] text-asg-slate">{hint}</p>}
  </div>;
});

const NOTICE_TONES = {
  info: { classes: 'bg-asg-lime-soft text-asg-forest-deep', icon: 'info' },
  success: { classes: 'bg-asg-lime-soft text-asg-forest-deep', icon: 'done' },
  warning: { classes: 'bg-asg-amber-soft text-asg-amber', icon: 'alert' },
  danger: { classes: 'bg-asg-danger-soft text-asg-danger', icon: 'alert' },
} as const;

export function Notice({ tone = 'info', id, children }: { tone?: keyof typeof NOTICE_TONES; id?: string; children: ReactNode }) {
  const { classes, icon } = NOTICE_TONES[tone];
  return <div id={id} role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
    className={`flex items-start gap-2 rounded-[14px] p-4 text-left text-[15px] font-medium leading-[21px] ${classes}`}>
    <Icon name={icon} className="mt-px h-5 w-5 shrink-0" />
    <div className="min-w-0 [overflow-wrap:anywhere]">{children}</div>
  </div>;
}

/** A thin rule with a label in the middle: "or", "or use your password". */
export function Divider({ label }: { label: string }) {
  return <div className="flex items-center gap-3 text-[13px] text-asg-slate">
    <span aria-hidden="true" className="h-px flex-1 bg-asg-line" />{label}<span aria-hidden="true" className="h-px flex-1 bg-asg-line" />
  </div>;
}

/** Progress bars, with the same information as text for screen readers. */
export function StepBars({ current, total }: { current: number; total: number }) {
  return <div className="flex justify-center gap-2">
    <span className="sr-only">Step {current} of {total}</span>
    {Array.from({ length: total }, (_, index) => <span key={index} aria-hidden="true"
      className={`h-1.5 w-7 rounded-full ${index < current ? 'bg-asg-forest' : 'bg-asg-line'}`} />)}
  </div>;
}

/** Large round picture at the top of each guide screen. */
export function Picture({ name }: { name: IconName }) {
  return <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-asg-lime-soft text-asg-forest">
    <Icon name={name} className="h-10 w-10" />
  </div>;
}

/** The roundel and name, small, above the guide. */
export function BrandMark() {
  return <div className="flex items-center justify-center gap-2 font-display text-[17px] font-semibold text-asg-forest">
    <img src="/asg-roundel.png" alt="" className="h-8 w-8" />A Shade Greener
  </div>;
}

/** Forest-green masthead on the signed-out screens, as in the app. */
export function BrandMasthead({ title, subtitle }: { title: string; subtitle: string }) {
  return <header className="rounded-b-[22px] bg-asg-forest px-6 pb-8 pt-6 text-white sm:rounded-[22px]">
    <a href="https://www.ashadegreener.co.uk" className="mb-6 inline-flex items-center gap-3 rounded-lg font-display text-[19px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
      <img src="/asg-roundel.png" alt="" className="h-11 w-11" />A Shade Greener
    </a>
    <h1 className="font-display text-[34px] font-bold leading-[1.08] tracking-[-0.03em] sm:text-[36px]">{title}</h1>
    <p className="mt-2 text-[17px] leading-6 text-asg-on-forest-muted">{subtitle}</p>
  </header>;
}

/** Full-page loading state in the sign-in style. */
export function AuthLoading() {
  return <div className="flex min-h-screen items-center justify-center bg-asg-paper">
    <div role="status" aria-label="Loading" className="h-10 w-10 animate-spin rounded-full border-[3px] border-asg-line border-t-asg-forest" />
  </div>;
}

export const headingClasses = {
  guide: 'font-display text-[30px] font-bold leading-[34px] tracking-[-0.013em] text-asg-ink',
  section: 'font-display text-[21px] font-semibold leading-[26px] tracking-[-0.02em] text-asg-ink',
};
